import { useState, useEffect } from 'react'
import styles from './AddMemberModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function AddMemberModal({ 
    isOpen, 
    onClose, 
    onSave, 
    apiToken = null,
    availableRoles = ['member', 'admin']
}) {
    const [formData, setFormData] = useState({
        email: '',
        role: 'member',
        first_name: '',
        last_name: ''
    })
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !isLoading)

    // Initialize form data when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                email: '',
                role: 'member',
                first_name: '',
                last_name: ''
            })
            setError('')
        }
    }, [isOpen])

    const parseEmailToNames = (email) => {
        if (!email || !email.includes('@')) return { first_name: '', last_name: '' }
        
        const username = email.split('@')[0]
        
        if (username.includes('.') || username.includes('-')) {
            // If there's a dot, split into first and last name
            const nameParts = username.split(/[.-]/)
            const firstName = capitalizeWord(nameParts[0])
            const lastName = capitalizeWord(nameParts[1])
            return { first_name: firstName, last_name: lastName }
        } else {
            // If no dot, use the whole username as first name
            const firstName = capitalizeWord(username)
            return { first_name: firstName, last_name: '' }
        }
    }

    const handleInputChange = (field, value) => {
        let updates = { [field]: value }
        
        // Auto-fill names when email changes and name fields are empty
        if (field === 'email') {
            const parsedNames = parseEmailToNames(value)
            
            // Only auto-fill if the name fields are currently empty
            if (!formData.first_name.trim()) {
                updates.first_name = parsedNames.first_name
            }
            if (!formData.last_name.trim()) {
                updates.last_name = parsedNames.last_name
            }
        }
        
        setFormData(prev => ({
            ...prev,
            ...updates
        }))
        
        // Clear error when user starts typing
        if (error) {
            setError('')
        }
    }

    const validateForm = () => {
        const { email, role } = formData

        if (!email.trim()) {
            setError('Email address is required')
            return false
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email.trim())) {
            setError('Please enter a valid email address')
            return false
        }

        if (!role || !availableRoles.includes(role)) {
            setError('Please select a valid role')
            return false
        }

        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        setIsLoading(true)
        setError('')

        try {
            if (!apiToken) {
                throw new Error('API token is required')
            }

            apiClient.setAuthToken(apiToken)
            
            const memberData = {
                email: formData.email.trim().toLowerCase(),
                role: formData.role,
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim()
            }

            const response = await apiClient.addTeammate(memberData)
            
            if (response.success) {
                onSave(response.teammate, response.message)
                onClose()
            } else {
                const errorMessage = response.error || 'Failed to add member'
                // Make the "already a member" message more user-friendly
                if (errorMessage.includes('already a member')) {
                    setError(`${formData.email} is already an active member of this workspace. You can view their details in the team members list.`)
                } else {
                    setError(errorMessage)
                }
            }
        } catch (err) {
            console.error('Failed to add member:', err)
            setError(err.message || 'Failed to add member. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleCancel = () => {
        if (isLoading) return
        onClose()
    }

    const capitalizeWord = (word) => {
        if (!word) return ''
        return word.charAt(0).toUpperCase() + word.slice(1)
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Add Team Member</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={isLoading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label htmlFor="email" className={styles.label}>
                            Email Address *
                        </label>
                        <input
                            type="email"
                            id="email"
                            className={styles.input}
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="Enter the user's email address"
                            autoFocus
                            disabled={isLoading}
                            required
                        />
                        <div className={styles.helpText}>
                            We'll add this user to your workspace. If they don't have an account yet, they'll be invited to join. If they were previously removed from the workspace, they'll be automatically reactivated.
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="first_name" className={styles.label}>
                                First Name
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                className={styles.input}
                                value={formData.first_name}
                                onChange={(e) => handleInputChange('first_name', e.target.value)}
                                placeholder="First name (optional)"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="last_name" className={styles.label}>
                                Last Name
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                className={styles.input}
                                value={formData.last_name}
                                onChange={(e) => handleInputChange('last_name', e.target.value)}
                                placeholder="Last name (optional)"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <div className={styles.helpText}>
                        Names will be overridden when the user signs in with Google.
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="role" className={styles.label}>
                            Role *
                        </label>
                        <select
                            id="role"
                            className={styles.select}
                            value={formData.role}
                            onChange={(e) => handleInputChange('role', e.target.value)}
                            disabled={isLoading}
                            required
                        >
                            {availableRoles.map(role => (
                                <option key={role} value={role}>
                                    {capitalizeWord(role)}
                                </option>
                            ))}
                        </select>
                        <div className={styles.helpText}>
                            {formData.role === 'admin' 
                                ? 'Admins can manage workspace members, projects, and settings.' 
                                : 'Members can access assigned projects and track time.'}
                        </div>
                    </div>

                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.buttonRow}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Adding Member...' : 'Add Member'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}